import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId, DINOS, DINO_ORDER } from '../data/dinos';
import { CommsBox, COMMS } from '../objects/CommsBox';

interface HandoffInit {
  /** Dino just finished a planet (zooms off). */
  prevDinoId: DinoId;
  /** Dino about to start the next planet (zooms in). */
  nextDinoId: DinoId;
  /** Score carried into the next segment. */
  runScore: number;
  /** Wall-clock timestamp the gauntlet's first leg started — threaded
   *  through to the finale so we can compute total run time. */
  runStartMs?: number;
}

/**
 * Between gauntlet segments: previous dino climbs onto a tiny rocket and
 * zooms off-screen-right, the planet palette swaps mid-fade, then the next
 * dino's rocket zooms in from the left and drops them off. Pure cosmetic
 * connective tissue; on completion it starts GameScene with the new dino.
 */
export class HandoffScene extends Phaser.Scene {
  private payload!: HandoffInit;

  constructor() {
    super('Handoff');
  }

  init(data: HandoffInit) {
    this.payload = data;
  }

  create() {
    const { prevDinoId, nextDinoId, runScore, runStartMs } = this.payload;
    const prev = DINOS[prevDinoId];
    const next = DINOS[nextDinoId];

    // Background — start in prev planet's tone, fade to next.
    const bg = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, prev.accent)
      .setOrigin(0, 0)
      .setAlpha(0.25);

    // Starfield.
    for (let i = 0; i < 50; i++) {
      this.add
        .image(Phaser.Math.Between(0, GAME_WIDTH), Phaser.Math.Between(0, GAME_HEIGHT), 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.3, 0.9));
    }

    // Banner texts (announces the handoff).
    const announce = this.add
      .text(GAME_WIDTH / 2, 50, `${prev.name}: STAGE CLEAR`, {
        fontFamily: '"VT323", monospace',
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const upNext = this.add
      .text(GAME_WIDTH / 2, 70, `NEXT UP — ${next.name} on ${next.planet}`, {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffe23a',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    // Run progress dots: filled = cleared, current = pulse, future = empty.
    // Boss-down cheer → Mr Consequence seethes about the planet we just
    // cleared → next dino's intro. Three-line sequence keeps the rivalry
    // building across all four planets in gauntlet mode. Each line takes
    // ~2.5s end-to-end (slide + type + hold + slide-out) so we can't rush
    // the handoff — see the comms-idle gate below.
    const comms = new CommsBox(this);
    comms.saySequence([
      COMMS.bossDefeated(),
      COMMS.villainDefeated(prevDinoId),
      COMMS.start(nextDinoId),
    ]);

    const stageIdx = DINO_ORDER.indexOf(nextDinoId);
    const dotsY = 100;
    DINO_ORDER.forEach((id, i) => {
      const cx = GAME_WIDTH / 2 + (i - 1.5) * 22;
      const filled = i < stageIdx;
      const dot = this.add
        .circle(cx, dotsY, 4, filled ? DINOS[id].stripe : 0x666688)
        .setStrokeStyle(1, 0xffffff, 0.5);
      if (i === stageIdx) {
        this.tweens.add({
          targets: dot,
          scale: { from: 1, to: 1.6 },
          alpha: { from: 1, to: 0.4 },
          duration: 600,
          yoyo: true,
          repeat: -1,
        });
      }
    });

    // Score readout
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 18, `score so far  ${String(runScore).padStart(4, '0')}`, {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffe23a',
      })
      .setOrigin(0.5)
      .setAlpha(0.9);

    const groundY = GAME_HEIGHT - 50;
    // Ground line for the rocket flyby.
    this.add.rectangle(0, groundY + 10, GAME_WIDTH, 2, 0xffffff, 0.3).setOrigin(0, 0);

    // PHASE 1 — prev dino zooms off-screen right.
    const exitY = groundY - 12;
    const prevRig = this.spawnRocket(80, exitY, prevDinoId, false);
    this.tweens.add({
      targets: prevRig.parts,
      x: '+=' + (GAME_WIDTH - 60 + 60), // off the right edge
      angle: -8,
      duration: 1100,
      delay: 350,
      ease: 'Cubic.easeIn',
    });

    // Mid-transition: fade bg from prev to next palette.
    this.tweens.add({
      targets: bg,
      fillColor: { from: prev.accent, to: next.accent },
      alpha: { from: 0.25, to: 0.4 },
      duration: 900,
      delay: 700,
    });

    // PHASE 2 — next dino zooms in from left.
    const nextRig = this.spawnRocket(-80, exitY, nextDinoId, true);
    this.tweens.add({
      targets: nextRig.parts,
      x: '+=' + (GAME_WIDTH / 2 - -80), // arrive at center
      angle: 0,
      duration: 1100,
      delay: 1500,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        // Tiny hop off the rocket.
        this.tweens.add({
          targets: nextRig.dino,
          y: exitY - 18,
          duration: 200,
          yoyo: true,
          ease: 'Sine.easeOut',
        });
        // Reveal "NEXT UP" line.
        this.tweens.add({
          targets: upNext,
          alpha: 1,
          duration: 250,
        });
      },
    });

    // PHASE 3 — start the next GameScene once comms are done playing OR the
    // player skips. The rocket sequence finishes around 2.6s; the comms run
    // for ~7-8s. We poll the comms for idle and transition then. SPACE or
    // click skips early. A safety cap of 11s prevents wedging if something
    // goes wrong with the comms queue.
    let transitioned = false;
    const goNext = () => {
      if (transitioned) return;
      transitioned = true;
      this.cameras.main.flash(220, 255, 255, 255);
      this.time.delayedCall(220, () => {
        this.scene.start('Game', {
          dinoId: nextDinoId,
          mode: 'gauntlet',
          runScore,
          runStartMs,
        });
      });
    };
    // Poll comms until idle, then transition.
    const waitForComms = this.time.addEvent({
      delay: 200,
      loop: true,
      callback: () => {
        if (comms.isIdle) {
          waitForComms.remove();
          goNext();
        }
      },
    });
    // Safety cap.
    this.time.delayedCall(11000, goNext);
    // Skip — show hint after the rockets land so it doesn't compete with
    // the prev-rocket-exiting animation. SPACE or click jumps to next level.
    this.time.delayedCall(2800, () => {
      if (transitioned) return;
      const skipHint = this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT - 32, 'SPACE / CLICK to skip', {
          fontFamily: '"VT323", monospace',
          fontSize: '9px',
          color: '#ffffff',
        })
        .setOrigin(0.5)
        .setAlpha(0);
      this.tweens.add({ targets: skipHint, alpha: 0.7, duration: 400 });
    });
    const skip = () => goNext();
    this.input.keyboard?.on('keydown-SPACE', skip);
    this.input.keyboard?.on('keydown-ENTER', skip);
    this.input.on('pointerdown', skip);

    // Announce intro pop.
    this.tweens.add({
      targets: announce,
      alpha: { from: 0, to: 1 },
      scale: { from: 1.4, to: 1 },
      duration: 400,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Builds a tiny rocket with a dino sitting on top. Returns refs for tweening.
   */
  private spawnRocket(x: number, y: number, dinoId: DinoId, facingRight: boolean) {
    const dir = facingRight ? 1 : -1;
    // Rocket body — narrow tall capsule
    const body = this.add.rectangle(x, y + 6, 22, 14, 0xc0c8d4).setStrokeStyle(1, 0x404858);
    // Nose
    const nose = this.add.triangle(x + dir * 14, y + 6, 0, -7, 0, 7, 8, 0, 0xffe23a);
    if (!facingRight) nose.setScale(-1, 1);
    // Fins
    const fin1 = this.add.triangle(x - dir * 12, y + 0, 0, 0, 0, 8, 5, 8, 0xff3a4a);
    const fin2 = this.add.triangle(x - dir * 12, y + 12, 0, 0, 0, -8, 5, -8, 0xff3a4a);
    if (!facingRight) {
      fin1.setScale(-1, 1);
      fin2.setScale(-1, 1);
    }
    // Flame
    const flame = this.add.triangle(
      x - dir * 16,
      y + 6,
      0, -4, 0, 4, -6 * dir, 0,
      0xffa600,
    );
    this.tweens.add({
      targets: flame,
      scaleX: { from: 1, to: 1.4 },
      scaleY: { from: 1, to: 0.7 },
      alpha: { from: 0.9, to: 0.5 },
      duration: 80,
      yoyo: true,
      repeat: -1,
    });
    // Dino on top
    const dino = this.add.sprite(x, y - 8, `dino-${dinoId}`).setScale(1.5).play(`${dinoId}-idle`);
    if (!facingRight) dino.setFlipX(true);
    return { dino, parts: [body, nose, fin1, fin2, flame, dino] };
  }
}
